import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import MasterFilter, { type FilterOption, useMasterFilter } from 'components/common/MasterFilter';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetCampaignSelectorQuery, useGetCampaignStatsQuery } from 'services/api/leads.api';
import { useGetMetaCampaignsWithInsightsQuery } from 'services/api/metaInsights.api';
import { NAVY, RADIUS_COIN, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import {
  DEFAULT_META_DATE_PRESET,
  isMetaDatePreset,
  type MetaDatePreset,
  metaDatePresetToRange,
} from 'utils/metaDatePresets';

import { campaignFilterFields, FILTER_KEYS } from './filters/campaignFilterFields';
import { useCampaignDynamicOptions } from './filters/useCampaignDynamicOptions';
import { buildCampaignRows } from './mappers/toCampaignRow';
import AdAccountSidebar from './sections/AdAccountSidebar';
import CampaignDetailDrawer from './sections/CampaignDetailDrawer';
import KpiStrip from './sections/KpiStrip';
import UnifiedCampaignTable from './sections/UnifiedCampaignTable';
import type { CampaignRow } from './types';

const cardSx = {
  borderRadius: RADIUS_COIN,
  boxShadow: SHADOW,
  border: 'none',
  bgcolor: SURFACE_CONTAINER_LOWEST,
} as const;

const CampaignsControlPanel: React.FC = () => {
  const { selectedBusinessUnitId: businessUnitId } = useSelectedBusinessUnit();

  const { metaAccounts, isLoading: loadingAccounts } = useCampaignDynamicOptions({ businessUnitId });

  const [whatsappSelected, setWhatsappSelected] = useState<boolean>(false);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);

  // Reset on BU switch — otherwise the right panel would keep showing the previous BU's
  // campaigns until the user manually untouches the checkboxes.
  const prevBuRef = useRef(businessUnitId);
  useEffect(() => {
    if (prevBuRef.current !== businessUnitId) {
      prevBuRef.current = businessUnitId;
      setSelectedAccountIds([]);
      setWhatsappSelected(false);
    }
  }, [businessUnitId]);

  const handleSelectionChange = useCallback((accountIds: string[], whatsapp: boolean) => {
    setSelectedAccountIds(accountIds);
    setWhatsappSelected(whatsapp);
  }, []);

  const wantsMeta = selectedAccountIds.length > 0;
  const selectedChannels = useMemo(() => {
    const chans: string[] = [];
    if (whatsappSelected) chans.push('whatsapp');
    if (wantsMeta) chans.push('meta');
    return chans;
  }, [whatsappSelected, wantsMeta]);

  const masterFilter = useMasterFilter(campaignFilterFields, businessUnitId, {});

  const selectedCampaignIds = useMemo<string[]>(() => {
    const v = masterFilter.values[FILTER_KEYS.campaignIds];
    return Array.isArray(v) ? (v as string[]) : [];
  }, [masterFilter.values]);

  const metaStatus = useMemo<'all' | 'active' | 'inactive'>(() => {
    const v = masterFilter.values[FILTER_KEYS.metaStatus];
    if (v === 'active' || v === 'inactive') return v;
    return 'all';
  }, [masterFilter.values]);

  const datePreset = useMemo<MetaDatePreset>(() => {
    const v = masterFilter.values[FILTER_KEYS.datePreset];
    return isMetaDatePreset(v) ? v : DEFAULT_META_DATE_PRESET;
  }, [masterFilter.values]);

  // Meta Insights gets the preset directly. The Lead-stats endpoint needs explicit
  // ISO dates, so derive the same window the preset would expand to server-side.
  const { start: presetStart, end: presetEnd } = useMemo(() => metaDatePresetToRange(datePreset), [datePreset]);
  const startIso = presetStart.toISOString();
  const endIso = presetEnd.toISOString();

  // Lightweight presence check so the sidebar can hide the WhatsApp entry when the BU
  // has no active WhatsApp campaigns in the window.
  const { data: whatsappSelector } = useGetCampaignSelectorQuery(
    {
      businessUnitId: businessUnitId || 0,
      startDate: startIso,
      endDate: endIso,
      campaignType: 'whatsapp',
    },
    { skip: !businessUnitId }
  );
  const whatsappAvailable = (whatsappSelector?.whatsAppCampaigns?.length ?? 0) > 0;

  // If WhatsApp becomes unavailable after being selected, clear it so the stats query
  // skips instead of firing against an empty campaign set.
  useEffect(() => {
    if (!whatsappAvailable && whatsappSelected) {
      setWhatsappSelected(false);
    }
  }, [whatsappAvailable, whatsappSelected]);

  const campaignType: 'whatsapp' | 'meta' | 'all' = useMemo(() => {
    if (whatsappSelected && wantsMeta) return 'all';
    if (whatsappSelected) return 'whatsapp';
    return 'meta';
  }, [wantsMeta, whatsappSelected]);

  const {
    data: campaignStats,
    isFetching: loadingStats,
    error: statsError,
  } = useGetCampaignStatsQuery(
    {
      businessUnitId: businessUnitId || 0,
      startDate: startIso,
      endDate: endIso,
      campaignType,
      metaAdAccountIds: wantsMeta ? selectedAccountIds : undefined,
    },
    { skip: !businessUnitId || selectedChannels.length === 0 }
  );

  const {
    data: metaInsightsData,
    isFetching: loadingInsights,
    error: insightsError,
  } = useGetMetaCampaignsWithInsightsQuery(
    {
      metaAdAccountIds: selectedAccountIds,
      datePreset,
      ...(metaStatus !== 'all' ? { isActive: metaStatus === 'active' } : {}),
    },
    { skip: !wantsMeta }
  );

  const allRows = useMemo(
    () =>
      buildCampaignRows({
        campaignStats,
        metaInsightBlocks: metaInsightsData?.results ?? [],
        channels: selectedChannels,
      }),
    [campaignStats, metaInsightsData, selectedChannels]
  );

  // Rows that pass every filter except the campaign-id picker itself. These are the
  // actual candidates the table will render; we also use them to populate the campaign
  // selector so the user can't pick options that would immediately vanish.
  const candidateRows = useMemo(() => {
    return allRows.filter((r) => {
      const hasActivity =
        r.channel === 'whatsapp'
          ? r.totalLeads > 0
          : (r.spend ?? 0) > 0 || (r.impressions ?? 0) > 0 || r.totalLeads > 0;

      if (!hasActivity) return false;

      if (r.channel !== 'meta') return true;
      if (r.status === 'unknown') return false;
      if (metaStatus === 'active') return r.status === 'active';
      if (metaStatus === 'inactive') return r.status === 'paused';
      return true;
    });
  }, [allRows, metaStatus]);

  const campaignOptions = useMemo<FilterOption[]>(() => {
    const seen = new Set<string>();
    const opts: FilterOption[] = [];
    candidateRows.forEach((r) => {
      if (seen.has(r.id)) return;
      seen.add(r.id);
      const channelTag = r.channel === 'meta' ? 'Meta' : 'WhatsApp';
      opts.push({ value: r.id, label: `${r.campaignName} · ${channelTag}` });
    });
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [candidateRows]);

  const mergedDynamicOptions = useMemo(() => ({ [FILTER_KEYS.campaignIds]: campaignOptions }), [campaignOptions]);

  const filteredRows = useMemo(() => {
    if (selectedCampaignIds.length === 0) return candidateRows;
    const set = new Set(selectedCampaignIds);
    return candidateRows.filter((r) => set.has(r.id));
  }, [candidateRows, selectedCampaignIds]);

  const [selectedRow, setSelectedRow] = useState<CampaignRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleRowClick = useCallback((row: CampaignRow) => {
    setSelectedRow(row);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const isLoading = loadingStats || loadingInsights || loadingAccounts;
  const hasError = Boolean(statsError || insightsError);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        px: 2,
        pt: 1,
        pb: 3,
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <MasterFilter {...masterFilter} dynamicOptions={mergedDynamicOptions} />
        {isLoading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 3,
              borderRadius: '0 0 4px 4px',
              bgcolor: 'transparent',
              '& .MuiLinearProgress-bar': { bgcolor: NAVY },
            }}
          />
        )}
      </Box>

      {!businessUnitId && (
        <Card sx={cardSx}>
          <CardContent>
            <Typography variant="body1" align="center" sx={{ color: alpha(NAVY, 0.5), fontFamily: 'Jost, sans-serif' }}>
              Selecciona una unidad de negocio para ver el panel.
            </Typography>
          </CardContent>
        </Card>
      )}

      {businessUnitId && hasError && (
        <Card sx={cardSx}>
          <CardContent>
            <Typography variant="body1" align="center" sx={{ color: alpha(NAVY, 0.6), fontFamily: 'Jost, sans-serif' }}>
              Hubo un error al cargar el panel. Verifica los filtros e inténtalo nuevamente.
            </Typography>
          </CardContent>
        </Card>
      )}

      {businessUnitId && !hasError && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 3fr' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <AdAccountSidebar
            accounts={metaAccounts}
            whatsappAvailable={whatsappAvailable}
            selectedAccountIds={selectedAccountIds}
            whatsappSelected={whatsappSelected && whatsappAvailable}
            isLoading={loadingAccounts}
            onSelectionChange={handleSelectionChange}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              minWidth: 0,
              height: { xs: 'auto', md: 'calc(100vh - 160px)' },
              minHeight: { md: 320 },
            }}
          >
            <KpiStrip rows={filteredRows} />

            {isLoading && allRows.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress sx={{ color: NAVY }} />
              </Box>
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  flex: 1,
                  minHeight: { xs: 420, md: 0 },
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'opacity 150ms ease',
                  opacity: isLoading ? 0.55 : 1,
                  pointerEvents: isLoading ? 'none' : 'auto',
                }}
              >
                <UnifiedCampaignTable rows={filteredRows} isLoading={isLoading} onRowClick={handleRowClick} />
                {isLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 999,
                      bgcolor: SURFACE_CONTAINER_LOWEST,
                      boxShadow: SHADOW,
                      pointerEvents: 'auto',
                    }}
                  >
                    <CircularProgress size={14} sx={{ color: NAVY }} />
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        color: NAVY,
                        fontSize: '0.72rem',
                        fontWeight: 600,
                      }}
                    >
                      Actualizando…
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      <CampaignDetailDrawer
        row={selectedRow}
        open={drawerOpen}
        onClose={handleDrawerClose}
        datePreset={datePreset}
        businessUnitId={businessUnitId}
      />
    </Box>
  );
};

export default CampaignsControlPanel;
